import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { LOADING_LABELS } from '@kexiaoman/shared';
import { adminApi } from '@/lib/api';

export function RoutesPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['routes'], queryFn: adminApi.listRoutes });
  const [adding, setAdding] = useState(false);

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => adminApi.toggleRoute(id, isActive),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['routes'] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold tracking-tight">模型路由</h2>
        <Button onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4 mr-1" />
          新增规则
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        按 specificity 排序应用：精准 user &gt; 精准 model &gt; wildcard；同级按 priority 降序。改动 5 分钟内生效（或用顶部"刷新缓存"立即生效）。
      </p>

      {adding && (
        <AddRouteForm
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            queryClient.invalidateQueries({ queryKey: ['routes'] });
          }}
        />
      )}

      {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}

      {data && data.length === 0 && !adding && (
        <p className="text-muted-foreground text-sm italic">尚无任何规则——所有模型请求按请求原值透传。</p>
      )}

      {data && data.length > 0 && (
        <Table>
          <THead>
            <TR>
              <TH>from_model</TH>
              <TH>to_model</TH>
              <TH>用户</TH>
              <TH>优先级</TH>
              <TH>有效</TH>
              <TH>备注</TH>
              <TH>操作</TH>
            </TR>
          </THead>
          <TBody>
            {data.map((r) => (
              <TR key={r.id}>
                <TD className="font-mono text-xs">{r.from_model}</TD>
                <TD className="font-mono text-xs">{r.to_model}</TD>
                <TD>
                  {r.user_id ? (
                    <>
                      <div className="text-xs">{r.user_phone ? `手机号：${r.user_phone}` : '手机号未设置'}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{r.user_id.slice(0, 8)}…</div>
                    </>
                  ) : '*'}
                </TD>
                <TD>{r.priority}</TD>
                <TD>{r.is_active ? '✅' : '—'}</TD>
                <TD className="text-xs text-muted-foreground">{r.note ?? ''}</TD>
                <TD>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggle.mutate({ id: r.id, isActive: !r.is_active })}
                    disabled={toggle.isPending}
                    className="min-w-[64px]"
                  >
                    {toggle.isPending ? LOADING_LABELS.submitting : r.is_active ? '停用' : '启用'}
                  </Button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}

function AddRouteForm({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [fromModel, setFromModel] = useState('');
  const [toModel, setToModel] = useState('');
  const [user, setUser] = useState('');
  const [priority, setPriority] = useState('0');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      adminApi.addRoute({
        from_model: fromModel.trim() || '*',
        to_model: toModel.trim(),
        user_id: user.trim() || null,
        priority: Number(priority) || 0,
        note: note.trim() || undefined,
      }),
    onSuccess: onDone,
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">新增路由规则</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <Row label="from_model (留空或 * = 任意)">
            <Input placeholder="gpt-5-codex 或 *" value={fromModel} onChange={(e) => setFromModel(e.target.value)} />
          </Row>
          <Row label="to_model *必填*">
            <Input placeholder="gpt-5.4" value={toModel} onChange={(e) => setToModel(e.target.value)} />
          </Row>
          <Row label="用户手机号或 UUID (可空)">
            <Input value={user} onChange={(e) => setUser(e.target.value)} />
          </Row>
          <Row label="优先级 (越大越优先)">
            <Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
          </Row>
          <Row label="备注">
            <Input placeholder="为什么建这条" value={note} onChange={(e) => setNote(e.target.value)} />
          </Row>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button onClick={() => add.mutate()} disabled={!toModel.trim() || add.isPending}>
            {add.isPending ? LOADING_LABELS.submitting : '创建'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
